import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  input,
} from "@angular/core";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";
import { MatMiniFabButton, MatIconButton } from "@angular/material/button";
import { MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatChip, MatChipRemove } from "@angular/material/chips";
import { MatDialog } from "@angular/material/dialog";
import { injectChatState } from "../../../../agent-ui";
import { DebugModeService } from "../../services/debug-mode.service";
import { HitlService } from "../../services/hitl.service";
import { AttachmentStateService } from "../../services/attachment-state.service";
import { FileUploadService } from "../../services/file-upload.service";
import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
  AttachedFile,
  rejectionReason,
} from "../../model/attachment.types";
import { ConfirmDeleteFileDialogComponent } from "./confirm-delete-file-dialog.component";

/**
 * Chat input bar: outlined textarea with send (matMiniFab) and attach button.
 * Supports attaching up to 3 files (each within the MyData upload limit) before submitting.
 * Rejected files (unsupported type, over the size limit) appear as error chips
 * explaining why — never a silent drop. Enter sends the message; Shift+Enter
 * inserts a new line.
 */
@Component({
  selector: "lib-chat-input",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkTextareaAutosize,
    MatMiniFabButton,
    MatIconButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatProgressSpinner,
    MatChip,
    MatChipRemove,
  ],
  templateUrl: "./chat-input.component.html",
  styleUrl: "./chat-input.component.scss",
})
export class ChatInputComponent implements OnDestroy {
  @ViewChild("fileInput") private fileInputRef!: ElementRef<HTMLInputElement>;

  /** True while the agent is processing — disables input and shows spinner. */
  readonly isLoading = input<boolean>(false);

  protected readonly chatState = injectChatState();

  protected readonly debugMode = inject(DebugModeService);

  private readonly hitlService = inject(HitlService);

  private readonly attachmentState = inject(AttachmentStateService);

  private readonly fileUploadService = inject(FileUploadService);

  private readonly dialog = inject(MatDialog);

  /** Local object URLs for image previews during upload — revoked on removal or destroy. */
  private readonly localPreviewUrls = new Map<string, string>();

  protected readonly isBlocked = computed(() => this.hitlService.isBlocked());

  protected readonly isUploading = computed(() => this.attachmentState.isUploading());

  protected readonly files = computed(() => this.attachmentState.files());

  protected readonly canAttach = computed(() => this.attachmentState.canAddMore() && !this.isDisabled());

  protected readonly isDisabled = computed(() => this.isLoading() || this.isBlocked() || this.isUploading());

  protected readonly acceptedTypes = [...ACCEPTED_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(",");

  protected readonly isFrontendToolsActive = computed(() => !!this.hitlService.frontendToolsPayload());

  protected readonly placeholder = computed(() => {
    if (this.isFrontendToolsActive()) return "Executing action…";
    if (this.isBlocked()) return "Respond to the request above…";
    if (this.isLoading()) return "Waiting for response…";
    return "Ask anything…";
  });

  ngOnDestroy(): void {
    this.localPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.localPreviewUrls.clear();
  }

  protected isImageAttachment(f: AttachedFile): boolean {
    return f.file.type.startsWith("image/") || f.contentBlock?.type === "image";
  }

  /** Returns the best available preview URL: remote (auth-decorated) after upload, local object URL while uploading. */
  protected getPreviewUrl(f: AttachedFile): string | null {
    if (f.contentBlock?.type === "image") {
      const raw = f.contentBlock.metadata?.preview_url ?? f.contentBlock.source.value;
      return this.fileUploadService.authenticatedUrl(raw);
    }
    return this.localPreviewUrls.get(f.localId) ?? null;
  }

  protected onEnter(event: KeyboardEvent): void {
    if (event.shiftKey) return;
    event.preventDefault();
    this.submit();
  }

  protected submit(): void {
    const value = this.chatState.inputValue().trim();
    const hasAttachments = this.attachmentState.hasFiles();
    if ((!value && !hasAttachments) || this.isDisabled()) return;
    this.chatState.submitInput(value);
  }

  protected onAttachClick(): void {
    this.fileInputRef?.nativeElement.click();
  }

  protected onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    input.value = "";

    for (const file of selected) {
      if (!this.attachmentState.canAddMore()) break;

      const rejection = rejectionReason(file);
      if (rejection) {
        console.error(`ChatInputComponent: file rejected — ${rejection}`);
        this.attachmentState.addFile({ localId: crypto.randomUUID(), file, status: "error", error: rejection });
        continue;
      }

      const localId = crypto.randomUUID();
      if (file.type.startsWith("image/")) {
        this.localPreviewUrls.set(localId, URL.createObjectURL(file));
      }
      this.attachmentState.addFile({ localId, file, status: "uploading" });

      const threadId = this.attachmentState.threadId();
      this.fileUploadService.upload(file, threadId).subscribe({
        next: (response) => {
          this.attachmentState.updateFile(localId, { status: "ready", contentBlock: response.content_block });
        },
        error: (err) => {
          this.attachmentState.updateFile(localId, { status: "error", error: this.extractErrorMessage(err) });
        },
      });
    }
  }

  private extractErrorMessage(err: unknown): string {
    const body = (err as { error?: unknown })?.error;
    if (body && typeof body === "object") {
      const msg = (body as Record<string, unknown>).error;
      if (typeof msg === "string" && msg) return msg;
    }
    const status = (err as { status?: number })?.status;
    return status ? `Upload failed (${status})` : "Upload failed";
  }

  protected removeFile(localId: string): void {
    const attachment = this.attachmentState.files().find((f) => f.localId === localId);

    if (attachment?.contentBlock?.type === "document") {
      this.dialog
        .open(ConfirmDeleteFileDialogComponent, { data: { filename: attachment.file.name } })
        .afterClosed()
        .subscribe((confirmed) => {
          if (!confirmed) return;
          this.fileUploadService
            .deleteFile(attachment.contentBlock!.metadata!.id as string, this.attachmentState.threadId())
            .subscribe({ error: (err) => console.error("ChatInputComponent: delete uploaded file failed", err) });
          this.cleanupLocalFile(localId);
        });
      return;
    }

    this.cleanupLocalFile(localId);
  }

  private cleanupLocalFile(localId: string): void {
    const url = this.localPreviewUrls.get(localId);
    if (url) {
      URL.revokeObjectURL(url);
      this.localPreviewUrls.delete(localId);
    }
    this.attachmentState.removeFile(localId);
  }
}
